import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { RouterLink } from '../../routes/components';
import { usePathname } from '../../routes/hooks';

import { useResponsive } from '../../hooks/use-responsive';

import { account } from '../../_mock/account';

import Logo from '../../components/logo';
import Scrollbar from '../../components/scrollbar';

import { NAV } from './config-layout';
import navConfig from './config-navigation';

import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useSelector } from 'react-redux';

// ----------------------------------------------------------------------

export default function Nav({ openNav, onCloseNav }) {
  const pathname = usePathname();

  const upLg = useResponsive('up', 'lg');

  const company = useSelector((state) => state.users.company);

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [eik, setEik] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');

  const sendMail = () => {
    window.open('mailto:info@schetito.bg');
  }

  const handleVersionChange = (event) => {
    const version = event.target.value;
    setSelectedVersion(version);
    if (version) {
      window.location.href = `https://${version}.schetito.bg`;
    }
  };

  useEffect(() => {
    if(company !== null) {
      setName(company.name);
      setType(company.type);
      setEik(company.eik);
    }
  }, [company]);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderAccount = (
    <Box
      sx={{
        my: 2.5,
        mx: 2,
        py: 2.5,
        px: 2.5,
        display: 'flex',
        borderRadius: 2,
        alignItems: 'flex-start',
        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        position: 'relative',
        boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
      }}
    >
      <Avatar 
        src={account.photoURL} 
        alt={name}
        sx={{
          width: 44,
          height: 44,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          fontWeight: 'bold',
          fontSize: '1.1rem',
          boxShadow: (theme) => `0 3px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
          flexShrink: 0,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ ml: 2, flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}>
          {name}
        </Typography>
        
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.2 }}>
          {type}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Chip 
            label={eik} 
            size="small" 
            variant="soft"
            color="primary"
            sx={{ 
              fontSize: '0.7rem',
              height: 18,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
          <Chip 
            label="Активен" 
            size="small" 
            color="success" 
            variant="soft"
            sx={{ 
              fontSize: '0.7rem',
              height: 18,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        </Stack>
      </Box>
    </Box>
  );

  const renderMenu = (
    <Stack component="nav" spacing={0.5} sx={{ px: 2 }}>
      {navConfig
        .filter(item => {
          if (company?.company_type === "свободна-професия" && item.title === "Разходи") {
            return false;
          }
          return true;
        })
        .map((item) => (
          <NavItem key={item.title} item={item} />
        ))}
    </Stack>
  );

  const renderUpgrade = (
    <Box sx={{ px: 2, pb: 3, mt: 32 }}>
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <FormControl fullWidth sx={{ mb: 1 }}>
            <Select
              value={selectedVersion}
              onChange={handleVersionChange}
              displayEmpty
              sx={{
                borderRadius: 1.5,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => alpha(theme.palette.warning.main, 0.5),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.warning.main,
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Изберете версия
                </Typography>
              </MenuItem>
              <MenuItem value="2025">v.2025</MenuItem>
              <MenuItem value="platform">v.2026</MenuItem>
            </Select>
          </FormControl>
        </Box>
    </Box>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100vh',
        '& .simplebar-content': {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Logo sx={{ width: 200 }} />
      </Box>

      {renderAccount}

      <Divider sx={{ mx: 2, mb: 1.5, borderStyle: 'dashed' }} />

      {renderMenu}

      <Box sx={{ flexGrow: 1 }} />

      {renderUpgrade}
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {upLg ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.WIDTH,
            borderRight: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
            backdropFilter: 'blur(8px)',
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
              background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
              backdropFilter: 'blur(8px)',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

// ----------------------------------------------------------------------

function NavItem({ item }) {
  const pathname = usePathname();

  const active = item.path === pathname;
  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
              sx={{
          minHeight: 44,
          borderRadius: 1,
          typography: 'body2',
          color: 'text.secondary',
          textTransform: 'capitalize',
          fontWeight: 'fontWeightMedium',
          mx: 0.5,
          mb: 0.25,
          transition: 'all 0.2s ease',
          position: 'relative',
          '&:hover': {
            background: (theme) => alpha(theme.palette.primary.main, 0.06),
            transform: 'translateX(2px)',
          },
          ...(active && {
            color: 'primary.main',
            fontWeight: 'fontWeightSemiBold',
            background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            '&:hover': {
              background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
              transform: 'translateX(2px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '50%',
              background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              borderRadius: '0 2px 2px 0',
            },
          }),
        }}
    >
      <Box 
        component="span" 
        sx={{ 
          width: 24, 
          height: 24, 
          mr: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: active ? 'primary.main' : 'text.secondary',
          transition: 'color 0.2s ease',
        }}
      >
        {item.icon}
      </Box>

      <Box component="span" sx={{ fontWeight: active ? 600 : 500 }}>
        {item.title}
      </Box>
    </ListItemButton>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
};
